import classNames from 'classnames';
import { useBackendImageURL } from '../hooks/useBackendDomain.js';
import React, { HTMLAttributes, useCallback, useEffect, useRef, useState } from 'react';
import { ImageSkeleton } from './ImageSkeleton';
import styles from './QuirkImage.module.css';

export interface QuirkImageProps extends HTMLAttributes<{}> {
	src?: string;
	fallback?: string | JSX.Element; // Fallback image url or element to render
	alt: string;
	contain?: boolean;
	style?: React.CSSProperties;
}

interface QuirkImageState {
	loading: boolean;
	error?: boolean;
}

export const QuirkImage: React.FC<QuirkImageProps> = ({
	src = '',
	fallback,
	alt,
	className,
	style,
	contain
}: QuirkImageProps) => {
	const imageRef = useRef<HTMLImageElement>(null);
	const backendURL = useBackendImageURL(src || '');

	const [{ loading, error }, setState] = useState<QuirkImageState>({
		loading: true,
		error: false
	});

	// eslint-disable-next-line react-hooks/exhaustive-deps
	useEffect(() => {
		if (loading && !error && imageRef.current?.complete) {
			setState({
				loading: false,
				error: false
			});
		}
	});

	const onError = useCallback(() => {
		setState({
			loading: false,
			error: true
		});
	}, []);

	const onLoad = useCallback(() => {
		setState({
			loading: false,
			error: false
		});
	}, []);

	useEffect(() => {
		if (backendURL !== '') {
			setState({ loading: true, error: false });
		} else {
			setState({ loading: false, error: true });
		}
	}, [backendURL]);

	return (
		<div
			className={classNames(
				styles.QuirkImage,
				className,
				loading ? styles.loading : '',
				contain ? styles.contain : ''
			)}
			style={style}
		>
			{loading && <ImageSkeleton />}
			{!error && <img ref={imageRef} onError={onError} onLoad={onLoad} src={backendURL} alt={alt} />}
			{error && typeof fallback !== 'string' && <div>{fallback}</div>}
			{error && typeof fallback === 'string' && <img src={fallback} alt={`fallback for ${alt}`} />}
		</div>
	);
};
